/* eslint-disable no-unused-vars */
// https://github.com/dbroadhurst/aws-cognito-react/tree/master
import {
  AuthenticationDetails,
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { isEmail } from './tools';
import { USERPOOL_ID, CLIENT_ID } from '../config';

const userPoolId = USERPOOL_ID;
const clientId = CLIENT_ID;

const poolData = {
  UserPoolId: `${userPoolId}`,
  ClientId: `${clientId}`,
};

const userPool = new CognitoUserPool(poolData);

let currentUser = userPool.getCurrentUser();

export function getCurrentUser() {
  return currentUser;
}

function getCognitoUser(username) {
  const userData = {
    Username: username,
    Pool: userPool,
  };
  const cognitoUser = new CognitoUser(userData);

  return cognitoUser;
}

export async function getSession() {
  if (!currentUser) {
    currentUser = userPool.getCurrentUser();
  }
  if (!currentUser) throw new Error('No current user');

  return new Promise((resolve, reject) => {
    currentUser.getSession((err, session) => {
      if (err) {
        reject(err);
      } else {
        resolve(session);
      }
    });
  }).catch((err) => {
    throw err;
  });
}

export async function signUpUserWithEmail(username, email, password) {
  return new Promise((resolve, reject) => {
    const attributeList = [
      new CognitoUserAttribute({
        Name: 'email',
        Value: email,
      }),
    ];

    userPool.signUp(username, password, attributeList, [], (err, res) => {
      if (err) {
        reject(err);
      } else {
        resolve(res);
      }
    });
  }).catch((err) => {
    throw err;
  });
}

export async function refreshSession(refreshToken) {
  return new Promise((resolve, reject) => {
    currentUser.refreshSession(refreshToken, (err, session) => {
      if (err) {
        console.log(err);
        reject(err);
      } else {
        resolve(session);
      }
    });
  });
}

export async function verifyCode(username, code) {
  return new Promise((resolve, reject) => {
    const cognitoUser = getCognitoUser(username);

    cognitoUser.confirmRegistration(code, true, (err, result) => {
      if (err) {
        reject(err);
      } else {
        resolve(result);
      }
    });
  }).catch((err) => {
    throw err;
  });
}

export async function signInWithEmail(username, password) {
  if (isEmail(username)) username = `${username}.`; // eslint-disable-line no-param-reassign
  return new Promise((resolve, reject) => {
    const authenticationData = {
      Username: username,
      Password: password,
    };
    const authenticationDetails = new AuthenticationDetails(authenticationData);

    currentUser = getCognitoUser(username);

    currentUser.authenticateUser(authenticationDetails, {
      onSuccess(res) {
        // console.log(' authentication result:', res);
        // Heap integration: Track user identity and properties
        const user = res?.idToken?.payload || {};
        if (window.heap) {
          window.heap.identify(user?.email || username?.slice(0, -1));
          window.heap.addUserProperties({
            username: user?.['cognito:username'] || username,
            email: user?.email || username?.slice(0, -1),
          });
        }

        resolve(res);
      },
      onFailure(err) {
        reject(err);
      },
      newPasswordRequired: (
        userAttributes,
        requiredAttributes,
      ) => {

      },
    });
  }).catch((err) => {
    throw err;
  });
}

export function signOut() {
  if (currentUser) {
    currentUser.signOut();
  }
}

function getUsernameFromIdToken(idToken) {
  if (!idToken) {
    throw new Error('ID token is missing');
  }

  const parts = idToken.split('.');
  if (parts.length !== 3) {
    throw new Error('Invalid JWT format');
  }

  const payload = JSON.parse(
    atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')),
  );

  // Preferred order (most stable first)
  return (
    payload['cognito:username']
    || payload.username
    || payload.email
    || payload.sub
  );
}

export async function setSignInUserSession(idToken, accessToken, refreshToken) {
  const usernameFromToken = getUsernameFromIdToken(idToken);
  const cognitoUser = new CognitoUser({ Username: usernameFromToken, Pool: userPool });
  const session = new CognitoUserSession({
    IdToken: new CognitoIdToken({ IdToken: idToken }),
    AccessToken: new CognitoAccessToken({ AccessToken: accessToken }),
    RefreshToken: new CognitoRefreshToken({ RefreshToken: refreshToken }),
  });

  cognitoUser.setSignInUserSession(session);
  return session;
}

export async function getAttributes() {
  return new Promise((resolve, reject) => {
    currentUser.getUserAttributes((err, attributes) => {
      if (err) {
        reject(err);
      } else {
        resolve(attributes);
      }
    });
  }).catch((err) => {
    throw err;
  });
}

export async function setAttribute(attribute) {
  return new Promise((resolve, reject) => {
    const attributeList = [];
    const res = new CognitoUserAttribute(attribute);
    attributeList.push(res);

    currentUser.updateAttributes(attributeList, (err, res1) => {
      if (err) {
        reject(err);
      } else {
        resolve(res1);
      }
    });
  }).catch((err) => {
    throw err;
  });
}

export async function sendCode(username) {
  return new Promise((resolve, reject) => {
    const cognitoUser = getCognitoUser(username);

    if (!cognitoUser) {
      reject(new Error(`could not find ${username}`));
      return;
    }

    cognitoUser.forgotPassword({
      onSuccess(res) {
        resolve(res);
      },
      onFailure(err) {
        reject(err);
      },
    });
  }).catch((err) => {
    throw err;
  });
}

export async function forgotPassword(username, code, password) {
  return new Promise((resolve, reject) => {
    const cognitoUser = getCognitoUser(username);

    if (!cognitoUser) {
      reject(new Error(`could not find ${username}`));
      return;
    }

    cognitoUser.confirmPassword(code, password, {
      onSuccess() {
        resolve('password updated');
      },
      onFailure(err) {
        reject(err);
      },
    });
  });
}

export async function changePassword(oldPassword, newPassword) {
  return new Promise((resolve, reject) => {
    currentUser.changePassword(oldPassword, newPassword, (err, res) => {
      if (err) {
        reject(err);
      } else {
        resolve(res);
      }
    });
  });
}
